var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
/**
 * Secondary bar component that sits below the main app bar.
 *
 * @slot - Content to display in the subbar (right side)
 */
export class KRSubbar extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Array of breadcrumb items to display
         */
        this.breadcrumbs = [];
        /**
         * Whether to show the menu icon
         */
        this.menu = false;
    }
    _handleMenuClick() {
        this.dispatchEvent(new CustomEvent('menu-click', {
            bubbles: true,
            composed: true
        }));
    }
    /**
     * Handles click on a breadcrumb link.
     * Dispatches a cancelable event so SPA routers can intercept.
     */
    _handleBreadcrumbClick(e, crumb, isCurrent) {
        // Don't navigate if it's the current/last breadcrumb
        if (isCurrent || !crumb.url) {
            e.preventDefault();
            return;
        }
        // Allow Ctrl/Cmd+click and middle-click to open in new tab without SPA navigation
        if (e.ctrlKey || e.metaKey || e.button === 1) {
            return;
        }
        const breadcrumbEvent = new CustomEvent('breadcrumb-click', {
            detail: { breadcrumb: crumb },
            bubbles: true,
            composed: true,
            cancelable: true,
        });
        this.dispatchEvent(breadcrumbEvent);
        // If a listener called preventDefault(), prevent the native navigation
        if (breadcrumbEvent.defaultPrevented) {
            e.preventDefault();
        }
    }
    render() {
        return html `
      ${this.menu ? html `
        <div class="menu" @click=${this._handleMenuClick}>
          <svg
            class="menu__icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line
              x1="3"
              y1="6"
              x2="21"
              y2="6"
            ></line>
            <line
              x1="3"
              y1="12"
              x2="21"
              y2="12"
            ></line>
            <line
              x1="3"
              y1="18"
              x2="21"
              y2="18"
            ></line>
          </svg>
        </div>
      ` : nothing}
      <div class="breadcrumbs">
        ${this.breadcrumbs.map((crumb, index) => {
            const isCurrent = index === this.breadcrumbs.length - 1;
            // The href gets the base href prepended so Cmd/Ctrl+click opens the right
            // URL in a new tab. crumb.url itself is left intact (base-relative) and
            // passed to Angular via breadcrumb-click — its Router applies the base
            // itself. e.g. base "/operations/" + crumb.url "/settings" ->
            // "/operations/settings".
            const baseHref = document.querySelector('base')?.getAttribute('href') || '/';
            let href = '#';
            if (crumb.url) {
                let path = crumb.url;
                if (crumb.url.startsWith('/')) {
                    path = crumb.url.slice(1);
                }
                href = baseHref + path;
                if (!baseHref.endsWith('/')) {
                    href = baseHref + '/' + path;
                }
            }
            return html `
            ${index > 0 ? html `<span class="breadcrumb-separator">/</span>` : ''}
            <a
              class=${classMap({
                'breadcrumb': true,
                'breadcrumb--current': isCurrent
            })}
              href=${href}
              @click=${(e) => this._handleBreadcrumbClick(e, crumb, isCurrent)}
            >${crumb.label}</a>
          `;
        })}
      </div>
      <div class="content">
        <slot></slot>
      </div>

    `;
    }
}
KRSubbar.styles = css `
    :host {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      height: 40px;
      background: #E2E9F6;
      border-bottom: 1px solid rgb(229, 229, 228);
      align-items: center;
      padding: 0 16px;
      width: 100%;
      flex-shrink: 0;
    }

    .breadcrumbs {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 13px;
      flex: 1;
    }

    .breadcrumb {
      color: #000000;
      text-decoration: none;
      cursor: pointer;
      font-size: 13px;
    }

    .breadcrumb:hover {
      text-decoration: underline;
    }

    .breadcrumb--current {
      cursor: default;
    }

    .breadcrumb--current:hover {
      text-decoration: none;
    }

    .breadcrumb-separator {
      color: #9ca3af;
      font-size: 12px;
    }

    .content {
      display: flex;
      align-items: center;
      margin-left: auto;
    }

    .menu {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      margin-right: 8px;
      cursor: pointer;
    }

    .menu__icon {
      width: 18px;
      height: 18px;
    }
  `;
__decorate([
    property({ type: Array })
], KRSubbar.prototype, "breadcrumbs", void 0);
__decorate([
    property({ type: Boolean })
], KRSubbar.prototype, "menu", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-subbar')) {
    customElements.define('kr-subbar', KRSubbar);
}
//# sourceMappingURL=subbar.js.map